/**
 * QuickSearchPalette - the CRM's Ctrl+K palette (docs/plans/crm-report-builder-search-collab-plan.md A2.2 to
 * A2.4 and B2.5; WP Q-4). Find a lead by name (words in any order), phone (any format) or email, see
 * the leads you opened lately, jump to a study or its recruitment list, or to any CRM page.
 *
 * - The box is a combobox over one listbox (aria-activedescendant); the sections are Recently viewed
 *   (an empty box), then Leads, Studies and Pages (quickSearchLogic buildSections decides what shows
 *   and in which order; every word it prints comes from there).
 * - Every row is a real link (an anchor), so Ctrl-click and middle-click open a new tab. Enter opens
 *   the highlighted row, Ctrl/Cmd+Enter opens it in a new tab and keeps the palette, the arrows move
 *   (wrapping, headers skipped), Esc closes (bubble phase; the capture phase belongs to the confirm
 *   dialog). Enter pressed before the answer for the text in the box has arrived waits for it and
 *   opens its first row, so a pasted number plus Enter opens that lead, never the previous text's.
 * - The text is sent 150 ms after the last keystroke, in a POST body (never a URL), and each
 *   keystroke aborts the request before it. "See all in Patients" carries the text to
 *   /crm/database in router state (pages/Contacts.tsx reads it), never in the URL.
 * - Recently viewed is per person on this device, lead ids only: read, resolved by the api (a merged
 *   lead comes back as its kept record, a hidden one not at all) and, on success only, written back.
 * - A modal at the modal tier. It renders in place, inside the CRM's font-inter wrapper, so the call
 *   dock (Z.callDock, mounted after the routes) stays above it exactly as it does above every other
 *   CRM modal. Below sm: it fills the screen with 44 px rows; from sm: it is a centred panel.
 * - Not audited (decision Q5 a): the rows carry names and numbers, never message text.
 *
 * QuickSearchProvider is the single mount: it owns the open state, the Ctrl+K listener
 * (useQuickSearchHotkey) and the palette, and closes it when the page changes. Wrap CRMLayout's
 * tree in it once; QuickSearchTrigger (the top-bar box) opens it through useQuickSearch, or, when
 * mounted outside the provider, through requestQuickSearch's window event.
 */
import { Fragment, createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowRight, ArrowUp, CornerDownLeft, FileText, FlaskConical, ListChecks, Loader2, Search, User, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LifecycleBadge from '../components/LifecycleBadge';
import { Badge, Z } from '../components/ui';
import { formatPhone, prettySlug, useIsCRMAdmin, useTeam } from '../lib';
import { showOutreachNav } from '../outreach/outreachSurfaces';
import { useSharedNavCounts } from '../useNavCounts';
import { isQuickSearchAborted, quickSearch, quickSearchErrorMessage, resolveRecent } from './api';
import {
  DEBOUNCE_MS,
  MIN_CHARS,
  QUICK_WORDS,
  browserRecentStore,
  buildSections,
  clipQuery,
  emptyMessage,
  firstOptionIndex,
  flattenSections,
  isQuickSearchHotkey,
  keepActive,
  leadFlags,
  leadName,
  leadStudyLabel,
  moveActive,
  optionAt,
  optionDomId,
  pageTitle,
  readRecent,
  recentAfterResolve,
  studyName,
  writeRecent,
} from './quickSearchLogic';
import type { QuickLead, QuickOption, QuickPageGates, QuickSearchResponse, QuickSearchViewState } from './types';
import useQuickSearchHotkey, { QUICK_SEARCH_DIALOG_ATTR, modifierWordHere } from './useQuickSearchHotkey';

// ---------------------------------------------------------------------------------------------
// Opening the palette from anywhere
// ---------------------------------------------------------------------------------------------

export interface QuickSearchControls {
  /** The palette is showing. */
  open: boolean;
  /** Show the palette, or give its box the keyboard again when it already shows. */
  openQuickSearch: () => void;
  closeQuickSearch: () => void;
}

const QuickSearchContext = createContext<QuickSearchControls | null>(null);

/** The palette's controls, or null outside QuickSearchProvider. */
export function useQuickSearch(): QuickSearchControls | null {
  return useContext(QuickSearchContext);
}

/** The window event QuickSearchProvider listens for (requestQuickSearch dispatches it). */
export const QUICK_SEARCH_OPEN_EVENT = 'crm:quick-search-open';

/** Open the palette from a component that is not inside QuickSearchProvider (a no-op without one). */
export function requestQuickSearch(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(QUICK_SEARCH_OPEN_EVENT));
}

/**
 * The single mount: the open state, the Ctrl+K / Cmd+K listener, the window event and the palette
 * itself. Mount it once, around CRMLayout's tree (children are optional, so a bare
 * <QuickSearchProvider /> anywhere inside the router works too). The palette closes when the page
 * changes; it opens afresh each time (an empty box and the recently viewed leads).
 */
export function QuickSearchProvider({ children }: { children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Bumped on every request to open, so an open palette gives its box the keyboard back.
  const [focusSignal, setFocusSignal] = useState(0);
  const openQuickSearch = useCallback(() => {
    setOpen(true);
    setFocusSignal((n) => n + 1);
  }, []);
  const closeQuickSearch = useCallback(() => setOpen(false), []);

  useQuickSearchHotkey(openQuickSearch);

  useEffect(() => {
    const onRequest = () => openQuickSearch();
    window.addEventListener(QUICK_SEARCH_OPEN_EVENT, onRequest);
    return () => window.removeEventListener(QUICK_SEARCH_OPEN_EVENT, onRequest);
  }, [openQuickSearch]);

  // Back, Forward or any other navigation takes the palette with it.
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const controls = useMemo<QuickSearchControls>(
    () => ({ open, openQuickSearch, closeQuickSearch }),
    [open, openQuickSearch, closeQuickSearch],
  );

  return (
    <QuickSearchContext.Provider value={controls}>
      {children}
      {open && <QuickSearchPalette onClose={closeQuickSearch} focusSignal={focusSignal} />}
    </QuickSearchContext.Provider>
  );
}

// ---------------------------------------------------------------------------------------------
// The palette
// ---------------------------------------------------------------------------------------------

/** The palette's chrome words (the rows' words come from quickSearchLogic). */
export const PALETTE_WORDS = {
  dialog: 'Quick search',
  input: 'Search leads, studies and pages',
  results: 'Search results',
  clear: 'Clear search',
  close: 'Close search',
  cancel: 'Cancel',
  esc: 'Esc',
  searching: 'Searching...',
  unassigned: 'Unassigned',
  move: 'to move',
  open: 'to open',
  newTab: 'new tab',
  toClose: 'to close',
} as const;

/** The count a screen reader hears when an answer arrives. */
export function resultsWords(n: number): string {
  return n === 1 ? '1 result' : `${n.toLocaleString()} results`;
}

const ROW =
  'flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2 text-left no-underline transition-colors duration-150 sm:min-h-0 sm:py-1.5';
const ROW_ACTIVE = 'bg-primary-50 dark:bg-primary-900/20';
const ICON = 'h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500';
const KBD =
  'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-gray-200 bg-gray-50 px-1 font-sans text-[11px] font-medium text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300';

/** A small round separator between a row's details (no typographic character in the source). */
function Dot() {
  return <span aria-hidden="true" className="h-1 w-1 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600" />;
}

export interface QuickSearchPaletteProps {
  onClose: () => void;
  /** Changes each time the palette is asked to open: the box takes the keyboard again. */
  focusSignal?: number;
}

/** Rendered only while open (QuickSearchProvider mounts it), so each opening starts afresh. */
export default function QuickSearchPalette({ onClose, focusSignal = 0 }: QuickSearchPaletteProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const isAdmin = useIsCRMAdmin();
  const navCounts = useSharedNavCounts();
  const { byId: teamById } = useTeam();
  const navigate = useNavigate();
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  // The sidebar's own answers to its two non-role gates; reads the one count poll and adds none.
  const isSuperAdmin = user?.role === 'super_admin';
  const outreach = showOutreachNav(isAdmin, navCounts.outreach);
  const gates = useMemo<QuickPageGates>(() => ({ isSuperAdmin, outreach }), [isSuperAdmin, outreach]);

  const [query, setQuery] = useState('');
  const [result, setResult] = useState<QuickSearchResponse | null>(null);
  // The text `result` answers (the box may have moved on since).
  const [resultText, setResultText] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<QuickLead[] | null>(null);
  // The option the person picked with the arrows or the mouse; null means the first option.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Where the keyboard was before the palette opened, to give it back on a close without navigating.
  const [returnFocus] = useState<HTMLElement | null>(() =>
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  /** The text scheduled or in flight. */
  const pendingTextRef = useRef<string | null>(null);
  /** The text the shown result answers. */
  const answeredTextRef = useRef<string | null>(null);
  const pendingEnterRef = useRef(false);
  const scrollToActiveRef = useRef(false);
  const navigatedRef = useRef(false);

  // ---- Recently viewed: readRecent, resolveRecent, then (on success only) writeRecent. ----
  useEffect(() => {
    const store = browserRecentStore();
    const ids = readRecent(store, userId);
    if (!ids.length) {
      setRecent([]);
      return undefined;
    }
    let alive = true;
    const controller = new AbortController();
    resolveRecent(ids, controller.signal).then(
      (leads) => {
        if (!alive) return;
        setRecent(leads);
        writeRecent(store, userId, recentAfterResolve(ids, readRecent(store, userId), leads));
      },
      (e) => {
        if (!alive || isQuickSearchAborted(e)) return;
        setRecent([]); // the list stays stored as it was; the box still searches
      },
    );
    return () => {
      alive = false;
      controller.abort();
    };
  }, [userId]);

  // ---- The search: debounced, one request at a time, each keystroke aborting the one before. ----
  const cancelSearch = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    pendingTextRef.current = null;
  }, []);

  const runSearch = useCallback((text: string) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    pendingTextRef.current = text;
    setSearching(true);
    quickSearch(text, controller.signal).then(
      (answer) => {
        if (controllerRef.current !== controller) return; // superseded or closed
        controllerRef.current = null;
        pendingTextRef.current = null;
        answeredTextRef.current = text;
        setResult(answer);
        setResultText(text);
        setError(null);
        setSearching(false);
      },
      (e) => {
        if (controllerRef.current !== controller) return;
        controllerRef.current = null;
        pendingTextRef.current = null;
        setSearching(false);
        if (isQuickSearchAborted(e)) return;
        answeredTextRef.current = null;
        setResult(null);
        setResultText(null);
        setError(quickSearchErrorMessage(e));
      },
    );
  }, []);

  useEffect(() => () => cancelSearch(), [cancelSearch]);

  const onQueryChange = (value: string) => {
    setQuery(value);
    setActiveKey(null);
    pendingEnterRef.current = false;
    const text = clipQuery(value);
    if (text.length < MIN_CHARS || text === answeredTextRef.current) {
      cancelSearch();
      setSearching(false);
      setError(null);
      return;
    }
    if (text === pendingTextRef.current) return; // the request for exactly this text is already on its way
    cancelSearch();
    setError(null);
    pendingTextRef.current = text;
    setSearching(true);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      runSearch(text);
    }, DEBOUNCE_MS);
  };

  // ---- What shows ----
  const text = clipQuery(query);
  const searchMode = text.length >= MIN_CHARS;
  const stale = searchMode && resultText !== text;
  const view: QuickSearchViewState = useMemo(
    () => ({ query, recent, result, isAdmin, gates }),
    [query, recent, result, isAdmin, gates],
  );
  const sections = useMemo(() => buildSections(view), [view]);
  const rows = useMemo(() => flattenSections(sections), [sections]);
  const activeIndex = keepActive(rows, activeKey);
  const activeOption = optionAt(rows, activeIndex);
  const optionCount = rows.filter((row) => row.type === 'option').length;
  const message = searching ? null : emptyMessage(view, sections);
  const showEmail = searchMode && !stale && result?.interpreted.kind === 'email';
  const now = Date.now();

  // ---- Opening a row ----
  const openOption = useCallback(
    (option: QuickOption, newTab: boolean) => {
      if (newTab) {
        // Router state cannot cross tabs: "See all" opens Patients without the text there.
        window.open(option.href, '_blank', 'noopener');
        return;
      }
      navigatedRef.current = true;
      onClose();
      if (option.kind === 'see_all') navigate(option.href, { state: option.state });
      else navigate(option.href);
    },
    [navigate, onClose],
  );

  const onEnter = (newTab: boolean) => {
    if (!newTab && activeKey === null && searchMode && stale) {
      // The list on screen answers an older text: wait for this one's answer, then open its first row.
      pendingEnterRef.current = true;
      if (timerRef.current !== null || pendingTextRef.current !== text) runSearch(text);
      return;
    }
    if (activeOption) openOption(activeOption, newTab);
  };

  useEffect(() => {
    if (!pendingEnterRef.current || searching) return;
    pendingEnterRef.current = false;
    if (stale) return; // it failed: the error line says so
    const option = optionAt(rows, activeIndex);
    if (option) openOption(option, false);
  }, [searching, stale, rows, activeIndex, openOption]);

  const move = (delta: number) => {
    const option = optionAt(rows, moveActive(rows, activeIndex, delta));
    if (!option) return;
    scrollToActiveRef.current = true;
    setActiveKey(option.key);
  };

  useEffect(() => {
    if (!scrollToActiveRef.current) return;
    scrollToActiveRef.current = false;
    const el = activeOption ? document.getElementById(optionDomId(activeOption.key)) : null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [activeOption]);

  // ---- Focus, scroll lock, closing ----
  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [focusSignal]);

  useEffect(
    () => () => {
      // Closed without opening anything: the keyboard goes back where it was.
      if (!navigatedRef.current && returnFocus && returnFocus.isConnected && returnFocus !== document.body) {
        returnFocus.focus();
      }
    },
    [returnFocus],
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // The box keeps the keyboard (clicks inside never move it). Should focus still end up outside,
  // Esc anywhere closes: bubble phase, and never a key another handler already claimed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const root = dialogRef.current;
    if (!root) return;
    const focusables = Array.from(root.querySelectorAll<HTMLElement>('input, button, [href], [tabindex]')).filter(
      (el) => el.tabIndex >= 0 && !el.hasAttribute('disabled'),
    );
    if (!focusables.length) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const inside = !!active && root.contains(active);
    if (e.shiftKey && (!inside || active === first)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (!inside || active === last)) {
      e.preventDefault();
      first.focus();
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    // A modal: the page behind never hears the palette's keys (the recruitment list's "/" included).
    e.stopPropagation();
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Escape') {
      // Bubble phase: a control inside that claimed this Esc (preventDefault) keeps it.
      if (e.defaultPrevented) return;
      e.preventDefault();
      close();
      return;
    }
    if (isQuickSearchHotkey(e)) {
      // Ctrl+K again: stay here (the browser's own search box must not open).
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
      return;
    }
    if (e.key === 'Tab') {
      trapTab(e);
      return;
    }
    // The buttons keep their own Enter and Space; the dialog itself holds the keyboard only after a
    // press on the list's empty space.
    if (e.target !== inputRef.current && e.target !== dialogRef.current) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnter(e.ctrlKey || e.metaKey);
    }
  };

  const keepFocusInBox = (e: ReactMouseEvent<HTMLDivElement>) => {
    // A press on a row, a heading or a button leaves the keyboard in the box. The box itself and
    // the list's own element (its scrollbar) keep their default.
    if (e.target !== inputRef.current && e.target !== listboxRef.current) e.preventDefault();
  };

  const onBackdrop = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) close();
  };

  const onRowClick = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    // A modified or middle click is the browser's (a new tab or window): the palette stays open.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    navigatedRef.current = true;
    onClose(); // the link itself navigates (with the see-all state)
  };

  const clearBox = () => {
    onQueryChange('');
    inputRef.current?.focus();
  };

  // ---- Rows ----
  const recruiterOf = (lead: QuickLead): string | null => {
    if (!lead.owner_user_id) return PALETTE_WORDS.unassigned;
    return teamById.get(lead.owner_user_id)?.name || null;
  };

  const renderOption = (option: QuickOption) => {
    const active = option.key === activeOption?.key;
    const dim = stale && option.kind !== 'page';
    const className = `${ROW} ${active ? ROW_ACTIVE : ''} ${dim ? 'opacity-60' : ''}`;
    const shared = {
      id: optionDomId(option.key),
      role: 'option',
      'aria-selected': active,
      tabIndex: -1,
      onClick: onRowClick,
      onMouseMove: () => {
        if (!active) setActiveKey(option.key);
      },
      className,
    } as const;

    if (option.kind === 'lead') {
      const lead = option.lead;
      const flags = leadFlags(lead, now);
      const contact =
        showEmail && lead.email ? lead.email : lead.phone_e164 ? formatPhone(lead.phone_e164) : lead.email;
      const details = [contact, leadStudyLabel(lead), recruiterOf(lead)].filter((d): d is string => !!d);
      return (
        <Link key={option.key} to={option.href} data-quick-option={option.key} {...shared}>
          <User className={ICON} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {leadName(lead)}
              </span>
              {flags.map((flag) => (
                <Badge key={flag.key} tone={flag.tone} size="sm" title={flag.title}>
                  {flag.label}
                </Badge>
              ))}
            </span>
            {details.length > 0 && (
              <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                {details.map((detail, i) => (
                  <Fragment key={i}>
                    {i > 0 && <Dot />}
                    <span className={`truncate ${i === 0 ? 'tabular-nums' : ''}`}>{detail}</span>
                  </Fragment>
                ))}
              </span>
            )}
          </span>
          {lead.lifecycle_status && <LifecycleBadge status={lead.lifecycle_status} className="flex-shrink-0" />}
        </Link>
      );
    }

    if (option.kind === 'study') {
      const name = studyName(option.study);
      if (option.target === 'recruit') {
        return (
          <Link key={option.key} to={option.href} data-quick-option={option.key} aria-label={`${name}: ${option.label}`} {...shared}>
            <ListChecks className={ICON} aria-hidden="true" />
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
              <span className="flex-shrink-0 text-sm text-gray-900 dark:text-gray-100">{option.label}</span>
              <Dot />
              <span className="truncate text-xs text-gray-500 dark:text-gray-400">{name}</span>
            </span>
          </Link>
        );
      }
      return (
        <Link key={option.key} to={option.href} data-quick-option={option.key} aria-label={`${name}: ${option.label}`} {...shared}>
          <FlaskConical className={ICON} aria-hidden="true" />
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{name}</span>
            {option.study.status && (
              <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">{prettySlug(option.study.status)}</span>
            )}
          </span>
          <span className="flex-shrink-0 text-xs font-medium text-primary-700 dark:text-primary-400">{option.label}</span>
        </Link>
      );
    }

    if (option.kind === 'page') {
      return (
        <Link key={option.key} to={option.href} data-quick-option={option.key} {...shared}>
          <FileText className={ICON} aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-gray-100">{pageTitle(option.page)}</span>
        </Link>
      );
    }

    return (
      <Link key={option.key} to={option.href} state={option.state} data-quick-option={option.key} {...shared}>
        <ArrowRight className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-primary-700 dark:text-primary-400">{option.label}</span>
      </Link>
    );
  };

  const hasOptions = firstOptionIndex(rows) !== -1;
  const live = searching ? '' : message ?? (hasOptions ? resultsWords(optionCount) : '');
  const modifier = modifierWordHere();
  const dialogAttrs = { [QUICK_SEARCH_DIALOG_ATTR]: '' };

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-start justify-center sm:overflow-y-auto sm:bg-black/50 sm:p-4 sm:pt-[12vh] sm:backdrop-blur-sm`}
      onMouseDown={onBackdrop}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={PALETTE_WORDS.dialog}
        tabIndex={-1}
        {...dialogAttrs}
        onKeyDown={onKeyDown}
        onMouseDown={keepFocusInBox}
        className="fixed inset-0 flex flex-col overflow-hidden bg-white focus:outline-none dark:bg-gray-800 sm:static sm:inset-auto sm:max-h-[70vh] sm:w-full sm:max-w-xl sm:rounded-xl sm:border sm:border-gray-200 sm:shadow-xl sm:dark:border-gray-700 lg:max-w-2xl"
      >
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 px-3 dark:border-gray-700">
          <Search className={ICON} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={PALETTE_WORDS.input}
            aria-autocomplete="list"
            aria-expanded={hasOptions}
            aria-controls={listboxId}
            aria-activedescendant={activeOption ? optionDomId(activeOption.key) : undefined}
            placeholder={QUICK_WORDS.placeholder}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="go"
            className="min-h-[52px] min-w-0 flex-1 bg-transparent text-base text-gray-900 placeholder-gray-400 focus:outline-none dark:text-gray-100 dark:placeholder-gray-500 sm:min-h-0 sm:py-3 sm:text-sm"
          />
          {searching && (
            <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          )}
          {query && (
            <button
              type="button"
              onClick={clearBox}
              aria-label={PALETTE_WORDS.clear}
              className="inline-flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-gray-200 sm:h-8 sm:min-h-0 sm:w-8 sm:min-w-0"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={close}
            aria-label={PALETTE_WORDS.close}
            className="inline-flex min-h-[44px] flex-shrink-0 items-center justify-center rounded-lg px-2 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-gray-700 sm:h-8 sm:min-h-0 sm:px-1"
          >
            <span className="sm:hidden">{PALETTE_WORDS.cancel}</span>
            <kbd className={`${KBD} hidden sm:inline-flex`}>{PALETTE_WORDS.esc}</kbd>
          </button>
        </div>

        {error && (
          <p role="alert" className="flex-shrink-0 px-4 pt-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </p>
        )}

        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={PALETTE_WORDS.results}
          aria-busy={searching || undefined}
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${hasOptions ? 'p-2' : ''}`}
        >
          {sections.map((section) => {
            const headingId = `${baseId}-${section.id}`;
            return (
              <div key={section.id} role="presentation" className="mb-1 last:mb-0">
                <div
                  id={headingId}
                  aria-hidden="true"
                  className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500"
                >
                  {section.title}
                </div>
                <div role="group" aria-labelledby={headingId}>
                  {section.options.map(renderOption)}
                </div>
              </div>
            );
          })}
        </div>

        {!hasOptions && (searching || message) && (
          <p className="flex-shrink-0 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {searching ? PALETTE_WORDS.searching : message}
          </p>
        )}

        <p className="sr-only" aria-live="polite">
          {live}
        </p>

        <div className="hidden flex-shrink-0 items-center gap-4 border-t border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400 sm:flex">
          <span className="inline-flex items-center gap-1">
            <kbd className={KBD}>
              <ArrowUp className="h-3 w-3" aria-hidden="true" />
            </kbd>
            <kbd className={KBD}>
              <ArrowDown className="h-3 w-3" aria-hidden="true" />
            </kbd>
            {PALETTE_WORDS.move}
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className={KBD}>
              <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
            </kbd>
            {PALETTE_WORDS.open}
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className={KBD}>{modifier} Enter</kbd>
            {PALETTE_WORDS.newTab}
          </span>
          <span className="ml-auto inline-flex items-center gap-1">
            <kbd className={KBD}>{PALETTE_WORDS.esc}</kbd>
            {PALETTE_WORDS.toClose}
          </span>
        </div>
      </div>
    </div>
  );
}
